import { useCallback, useMemo } from "react";

import {
  MaterialLimitRead,
  MaterialLimitSet,
  useGetChefGroupingQuery,
  useGetMaterialLimitSetQuery,
  useGetProductGroupingQuery,
} from "src/store/api/generatedApi";
import { materialLimitKey } from "src/utils/materialLimitOverrides";
import { useTenant } from "./settings";

export interface ChefGroupInfo {
  name: string;
  productGroupId: number;
  productGroupName: string;
  steelGradeNames: string[];
}

const boundsAndHardnessDiffer = (
  a: MaterialLimitRead,
  b: MaterialLimitRead
): boolean =>
  a.hardness !== b.hardness ||
  JSON.stringify(a.bounds) !== JSON.stringify(b.bounds);

export const useDefaultMaterialLimits = () => {
  const tenantName = useTenant();

  const customerQuery = useGetMaterialLimitSetQuery({
    tenantName,
    materialLimitSetId: "default",
    source: "customer",
  });
  const scrapchefQuery = useGetMaterialLimitSetQuery({
    tenantName,
    materialLimitSetId: "default",
    source: "scrapchef",
  });

  const activeSet: MaterialLimitSet | undefined =
    customerQuery.data ?? scrapchefQuery.data;
  const hasCustomerDefault = customerQuery.data?.source === "customer";

  // An omitted source resolves to the tenant's active default chef grouping
  // (customer if one exists, else scrapchef) — the same resolution the
  // backend applies to each limit's chef_group_id, so rows line up.
  const chefGroupingQuery = useGetChefGroupingQuery({
    tenantName,
    chefGroupingId: "default",
  });
  const productGroupingQuery = useGetProductGroupingQuery(
    chefGroupingQuery.data
      ? {
          tenantName,
          productGroupingId: chefGroupingQuery.data.product_grouping_id,
        }
      : { tenantName, productGroupingId: "default" }
  );

  const productGroupNamesById = useMemo(() => {
    const productGroups = productGroupingQuery.data?.product_groups ?? [];
    return productGroups
      .filter(
        (group): group is typeof group & { id: number } => group.id != null
      )
      .reduce<Record<number, string>>((names, group) => {
        names[group.id] = group.name;
        return names;
      }, {});
  }, [productGroupingQuery.data]);

  const chefGroupsById = useMemo(() => {
    const chefGroups = chefGroupingQuery.data?.chef_groups ?? [];
    return chefGroups
      .filter(
        (group): group is typeof group & { id: number } => group.id != null
      )
      .reduce<Record<number, ChefGroupInfo>>((groups, group) => {
        groups[group.id] = {
          name: group.name,
          productGroupId: group.product_group_id,
          productGroupName: productGroupNamesById[group.product_group_id] ?? "",
          steelGradeNames: group.steel_grades.map((grade) => grade.name),
        };
        return groups;
      }, {});
  }, [chefGroupingQuery.data, productGroupNamesById]);

  const scrapchefLimitsByKey = useMemo(() => {
    const limits = scrapchefQuery.data?.material_limits ?? [];
    return limits.reduce<Record<string, MaterialLimitRead>>((byKey, limit) => {
      byKey[materialLimitKey(limit)] = limit;
      return byKey;
    }, {});
  }, [scrapchefQuery.data]);

  const showScrapchefWarning = useMemo(() => {
    const customer = customerQuery.data;
    const scrapchef = scrapchefQuery.data;
    if (
      !hasCustomerDefault ||
      customer === undefined ||
      scrapchef === undefined
    ) {
      return false;
    }
    const scrapchefCreated = Date.parse(scrapchef.created_at);
    const customerCreated = Date.parse(customer.created_at);
    if (
      !Number.isFinite(scrapchefCreated) ||
      !Number.isFinite(customerCreated) ||
      scrapchefCreated <= customerCreated
    ) {
      return false;
    }
    const customerByKey = new Map(
      customer.material_limits.map((limit) => [materialLimitKey(limit), limit])
    );
    if (customerByKey.size !== scrapchef.material_limits.length) {
      return true;
    }
    return scrapchef.material_limits.some((scrapchefLimit) => {
      const customerLimit = customerByKey.get(materialLimitKey(scrapchefLimit));
      return (
        customerLimit === undefined ||
        boundsAndHardnessDiffer(scrapchefLimit, customerLimit)
      );
    });
  }, [hasCustomerDefault, customerQuery.data, scrapchefQuery.data]);

  const refetch = useCallback(async () => {
    await Promise.all([customerQuery.refetch(), scrapchefQuery.refetch()]);
  }, [customerQuery, scrapchefQuery]);

  return {
    activeSet,
    hasCustomerDefault,
    chefGroupsById,
    scrapchefLimitsByKey,
    showScrapchefWarning,
    isLoading:
      customerQuery.isLoading ||
      scrapchefQuery.isLoading ||
      chefGroupingQuery.isLoading ||
      productGroupingQuery.isLoading,
    isFetching:
      customerQuery.isFetching ||
      scrapchefQuery.isFetching ||
      chefGroupingQuery.isFetching ||
      productGroupingQuery.isFetching,
    isError: customerQuery.isError && scrapchefQuery.isError,
    refetch,
  };
};
